import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Mail, Phone, Briefcase, Trash2, Pencil } from "lucide-react";
import AvatarUpload from "@/components/AvatarUpload";

export const AgencyRepRow = ({
  r, clientId, onEdit, onSetPrimary, onRemove, onUploadAvatar, onClearAvatar,
}) => (
  <Card
    className={`rounded-sm transition-colors ${r.is_primary ? "border-[#002FA7]/40 bg-[#F4F5FB]" : "border-zinc-200"}`}
    data-testid={`agency-rep-row-${r.rep_id}`}>
    <CardContent className="p-4 flex items-center gap-4 flex-wrap">
      <label
        className="inline-flex items-center gap-2 cursor-pointer"
        title="Set as primary team contact"
        data-testid={`agency-rep-primary-radio-${r.rep_id}`}>
        <input
          type="radio"
          name={`primary-rep-${clientId}`}
          checked={!!r.is_primary}
          onChange={() => onSetPrimary(r)}
          className="h-4 w-4"
        />
        <span className="text-[10px] font-mono uppercase tracking-wide text-zinc-500">
          {r.is_primary ? "Primary" : "Set primary"}
        </span>
      </label>
      <AvatarUpload
        url={r.avatar_url}
        initials={`${(r.first_name||"?")[0]}${(r.last_name||"")[0]}`}
        onUpload={(file) => onUploadAvatar(r, file)}
        onClear={r.avatar_url ? () => onClearAvatar(r) : null}
        size={48}
        testid={`agency-rep-avatar-${r.rep_id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-sm">{r.name}</span>
          {r.is_primary && (
            <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#002FA7] text-white"
              data-testid={`agency-rep-primary-badge-${r.rep_id}`}>
              Primary
            </span>
          )}
          {r.title && (
            <span className="inline-flex items-center text-[11px] text-zinc-600 gap-1">
              <Briefcase className="h-3 w-3" /> {r.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5 flex-wrap">
          {r.email && (
            <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-zinc-900">
              <Mail className="h-3 w-3" /> {r.email}
            </a>
          )}
          {r.phone && (
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-900">
              <Phone className="h-3 w-3" /> {r.phone}
            </a>
          )}
        </div>
      </div>
      <Button size="sm" variant="ghost" className="rounded-sm"
        onClick={() => onEdit(r)}
        data-testid={`agency-rep-edit-${r.rep_id}`}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
            data-testid={`agency-rep-delete-${r.rep_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {r.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll no longer appear on this client&apos;s PDF reports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
              onClick={() => onRemove(r)}
              data-testid={`agency-rep-confirm-delete-${r.rep_id}`}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CardContent>
  </Card>
);
